// Membership Card Component
// Simplified membership summary + credits + (mock) apps/storage + Manage Plan button

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isInGrace, getGraceCountdown } from '../../../utils/membership';
import './MembershipCard.css';

// Inline banner shown when the user's renewal payment failed and they are
// inside the 4-day grace window. Surfaces:
//   1. A countdown to graceUntil so the user understands the deadline.
//   2. "Update payment method" → opens the Stripe Customer Portal (existing
//      flow on `accountManager.handleManageSubscription`).
//   3. "Retry payment now" → calls /api/stripe/payment-method/retry-payment to
//      pay the open invoice with the user's current default PM. Only enabled
//      when membership.lastFailedInvoiceId is present.
function PastDueBanner({ membership, onUpdatePaymentMethod }) {
  const [retryLoading, setRetryLoading] = useState(false);
  const [retryMessage, setRetryMessage] = useState(null);

  if (!isInGrace(membership)) {
    return null;
  }

  const { daysRemaining, hoursRemaining, graceUntil } = getGraceCountdown(membership);
  const lastFailedInvoiceId = membership?.lastFailedInvoiceId || null;

  let countdownLabel;
  if (daysRemaining > 1) {
    countdownLabel = `${daysRemaining} days`;
  } else if (daysRemaining === 1) {
    countdownLabel = '1 day';
  } else if (hoursRemaining > 0) {
    countdownLabel = `${hoursRemaining} hour${hoursRemaining === 1 ? '' : 's'}`;
  } else {
    countdownLabel = 'less than an hour';
  }

  const handleRetryPayment = async () => {
    if (retryLoading) return;
    setRetryLoading(true);
    setRetryMessage(null);
    try {
      const response = await fetch('/api/stripe/payment-method/retry-payment', {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        const errMsg = data?.error || 'Payment retry failed.';
        setRetryMessage({ type: 'error', text: errMsg });
        return;
      }
      if (data.alreadyPaid) {
        setRetryMessage({
          type: 'success',
          text: 'Invoice was already paid. Refresh in a moment to see your account back to active.',
        });
        return;
      }
      setRetryMessage({
        type: 'success',
        text: data.message || 'Payment successful. Your account will return to active shortly.',
      });
    } catch (err) {
      setRetryMessage({
        type: 'error',
        text: err?.message || 'Network error. Please try again.',
      });
    } finally {
      setRetryLoading(false);
    }
  };

  return (
    <div
      role="alert"
      style={{
        margin: '0 0 12px 0',
        padding: '12px 14px',
        background: 'rgba(255, 152, 0, 0.12)',
        border: '1px solid rgba(255, 152, 0, 0.45)',
        borderRadius: '8px',
        color: '#ffd9a8',
        fontSize: '0.9rem',
        lineHeight: 1.4,
      }}
    >
      <div style={{ fontWeight: 600, color: '#ffb84d', marginBottom: '4px' }}>
        Payment failed for your plan
      </div>
      <div style={{ marginBottom: '10px' }}>
        Your last renewal payment didn&apos;t go through. Update your payment method within{' '}
        <strong>{countdownLabel}</strong> to keep your plan
        {graceUntil ? ` (until ${graceUntil.toLocaleString()})` : ''}.
        After that, your account will switch to the free plan.
      </div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => onUpdatePaymentMethod?.()}
          style={{
            padding: '8px 14px',
            background: 'rgba(255, 152, 0, 0.85)',
            border: '1px solid rgba(255, 152, 0, 1)',
            borderRadius: '6px',
            color: '#1a1306',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '0.85rem',
          }}
        >
          Update payment method
        </button>
        <button
          type="button"
          onClick={handleRetryPayment}
          disabled={!lastFailedInvoiceId || retryLoading}
          title={
            !lastFailedInvoiceId
              ? 'No open invoice on file. Try updating your payment method first.'
              : undefined
          }
          style={{
            padding: '8px 14px',
            background: 'rgba(255, 255, 255, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            borderRadius: '6px',
            color: !lastFailedInvoiceId || retryLoading ? '#888' : '#fff',
            fontWeight: 600,
            cursor: !lastFailedInvoiceId || retryLoading ? 'not-allowed' : 'pointer',
            fontSize: '0.85rem',
          }}
        >
          {retryLoading ? 'Retrying…' : 'Retry payment now'}
        </button>
      </div>
      {retryMessage ? (
        <div
          style={{
            marginTop: '10px',
            color: retryMessage.type === 'error' ? '#ff8a80' : '#a5e3a5',
            fontSize: '0.82rem',
          }}
        >
          {retryMessage.text}
        </div>
      ) : null}
    </div>
  );
}

function normalizePlanId(planId) {
  const value = String(planId || '').toLowerCase();
  if (value === 'free') return 'FREE';
  if (value === 'pro') return 'PRO';
  if (value === 'max') return 'MAX';
  if (value) return value.toUpperCase();
  return 'FREE';
}

function formatBillingCycleLabel(billingCycle) {
  const value = String(billingCycle || '').toLowerCase();
  if (value === 'annual') return 'Annual Plan';
  if (value === 'monthly') return 'Monthly Plan';
  if (value === 'one-time' || value === 'onetime') return 'One-Time Plan';
  return value ? `${value} Plan` : 'Plan';
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/**
 * Convert bytes to human-readable format with appropriate unit (B, KB, MB, GB, TB)
 * @param {number} bytes - Number of bytes
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted string like "4.8 KB" or "1.2 GB"
 */
function formatBytes(bytes, decimals = 1) {
  if (bytes === null || bytes === undefined || bytes === 0) {
    return '0 B';
  }

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
}

function ProgressRow({ label, valueText, percent, isLoading }) {
  const normalized = Number.isFinite(percent) ? clamp(percent, 0, 100) : 0;

  return (
    <div className="membership-card__row membership-card__row--progress">
      <span className="membership-card__label">{label}</span>
      <div
        className="membership-card__progress"
        role="progressbar"
        aria-label={label}
        aria-valuenow={isLoading ? undefined : Math.round(normalized)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="membership-card__progress-track" />
        {!isLoading && (
          <div
            className="membership-card__progress-fill"
            style={{ width: `${normalized}%` }}
          />
        )}
        <div className="membership-card__progress-text">
          {isLoading ? 'Loading...' : valueText}
        </div>
      </div>
    </div>
  );
}

export function MembershipCard({ user, membershipDetails, usage, onManageSubscription }) {
  // The membership object can come from a couple of places depending on where
  // the card is rendered. Prefer the live `user.membership` because that is
  // the same shape the backend writes graceUntil / lastFailedInvoiceId onto.
  const liveMembership = user?.membership || membershipDetails || null;
  const sectionRef = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);
  const planName = normalizePlanId(membershipDetails?.planId || user?.membership?.planId || 'free');
  const billingLabel = formatBillingCycleLabel(membershipDetails?.billingCycle || user?.membership?.billingCycle || '');

  // All three bars load together from usage prop - show loading state until usage is available
  const isLoading = !usage;

  const currentCredits = Number(usage?.credits?.currentCredits ?? 0);
  const monthlyCredits = Number(usage?.credits?.monthlyCredits ?? 0);
  const creditsPercent = monthlyCredits > 0 ? (currentCredits / monthlyCredits) * 100 : 0;

  const appsUsed = Number(usage?.apps?.used ?? 0);
  const appsAllowed = Number(usage?.apps?.allowed ?? 0);
  const appsPercent = appsAllowed > 0 ? (appsUsed / appsAllowed) * 100 : 0;

  // Get storage values in bytes (more accurate than GB for small values)
  const storageUsedBytes = Number(usage?.storage?.usedBytes ?? 0);
  const storageAllowedBytes = usage?.storage?.allowedBytes !== null && usage?.storage?.allowedBytes !== undefined 
    ? Number(usage.storage.allowedBytes) 
    : null; // null means unlimited
  
  // Calculate percentage for progress bar (using bytes for accuracy)
  const storagePercent = storageAllowedBytes !== null && storageAllowedBytes > 0 
    ? (storageUsedBytes / storageAllowedBytes) * 100 
    : 0;
  
  // Format for display
  const storageUsedFormatted = formatBytes(storageUsedBytes);
  const storageAllowedFormatted = storageAllowedBytes !== null 
    ? formatBytes(storageAllowedBytes)
    : 'Unlimited';

  const measureLabelWidth = () => {
    const root = sectionRef.current;
    if (!root) return;
    const labels = root.querySelectorAll('.membership-card__label');
    let max = 0;
    labels.forEach((el) => {
      max = Math.max(max, el.offsetWidth || 0);
    });
    setLabelWidth(max);
  };

  useLayoutEffect(() => {
    measureLabelWidth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planName, billingLabel]);

  useEffect(() => {
    const handleResize = () => measureLabelWidth();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="account-card membership-card">
      <div className="card-header membership-card__header">
        <div className="card-title membership-card__plan">{planName}</div>
        <div className="membership-card__billing">{billingLabel}</div>
      </div>

      <div className="card-body membership-card__body">
        <PastDueBanner
          membership={liveMembership}
          onUpdatePaymentMethod={onManageSubscription}
        />
        <div
          ref={sectionRef}
          className="membership-card__section"
          style={labelWidth > 0 ? { '--membership-card-label-width': `${labelWidth}px` } : undefined}
        >
          <ProgressRow
            label="Credits"
            valueText={`${currentCredits.toLocaleString()} / ${monthlyCredits.toLocaleString()}`}
            percent={creditsPercent}
            isLoading={isLoading}
          />

          <ProgressRow
            label="Apps"
            valueText={`${appsUsed} / ${appsAllowed}`}
            percent={appsPercent}
            isLoading={isLoading}
          />

          <ProgressRow
            label="Storage"
            valueText={`${storageUsedFormatted} / ${storageAllowedFormatted}`}
            percent={storagePercent}
            isLoading={isLoading}
          />
        </div>

        <div className="membership-card__actions">
          <button
            onClick={() => onManageSubscription?.()}
            style={{
              width: '100%',
              padding: '12px 24px',
              background: 'rgba(100, 100, 100, 0.3)',
              border: '1px solid rgba(100, 100, 100, 0.5)',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            Manage Plan
          </button>
        </div>
      </div>
    </div>
  );
}
